import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import { Worker, Viewer } from '@react-pdf-viewer/core'
import '@react-pdf-viewer/core/lib/styles/index.css'
import {
  IMAGE_BASE_PATH_DBRALC,
  ORGANIZATION_ID,
  SUB_ORGANIZATION_ID,
} from '../../Config/Config'
import { fetchData } from '../../Assets/API/apiservices'

const FileViewer = ({
  filenameKey = 'filename',
  backPath,
  allowDownload = false,
  autoDownload = false,
}) => {
  const [selectedDocData, setSelectedDocData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const navigate = useNavigate()
  const { docID, id } = useParams()
  const location = useLocation()
  const orgId = ORGANIZATION_ID || 10
  const subOrgId = SUB_ORGANIZATION_ID || 12

  console.log('docID:', docID, 'id:', id)
  console.log('selectedDocData', selectedDocData)

  const canDownload =
    typeof allowDownload === 'string'
      ? allowDownload.toLowerCase() === 'true'
      : allowDownload

  const shouldAutoDownload =
    typeof autoDownload === 'string'
      ? autoDownload.toLowerCase() === 'true'
      : autoDownload

  const handleDownload = async () => {
    console.log('Downloading file...')
    const fileUrl = selectedDocData?.pathurl || selectedDocData?.path_url
    const fullFileUrl = fileUrl ? `${IMAGE_BASE_PATH_DBRALC}${fileUrl}` : ''

    if (!fullFileUrl) {
      console.error('No file URL available for download')
      return
    }

    setDownloading(true)

    try {
      // Fetch the file as a blob
      const response = await fetch(fullFileUrl)
      if (!response.ok) throw new Error('Failed to fetch file')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        selectedDocData?.[filenameKey] || 'document'
      )
      document.body.appendChild(link)
      link.click()

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setDownloading(false)

        // Navigate back after download if autoDownload is enabled
        if (shouldAutoDownload) {
          handleBackClick()
        }
      }, 100)
    } catch (error) {
      console.error('Download failed:', error)
      setDownloading(false)
    }
  }

  useEffect(() => {
    const fetchDocumentData = async () => {
      setLoading(true)

      try {
        if (location.state?.selectedDocData) {
          setSelectedDocData(location.state.selectedDocData)
        } else {
          let fallbackData = null

          // Check from route if it's a newsletter or note
          if (location.pathname.startsWith('/alcnewsletter')) {
            const newsletters = await import(
              '../../Assets/JSON/Alcnewsletter/alcnewsletter.json'
            )
            const allDocuments = newsletters.default.flatMap(
              (yearGroup) => yearGroup.documents
            )
            fallbackData = allDocuments.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/faculty')) {
            const FacultyData = await import('../../Assets/JSON/MV.json')
            const allFaculty = FacultyData.default.FacultyData.Faculty || []
            console.log('allFaculty', allFaculty)
            console.log('docID', docID)

            fallbackData = allFaculty.find((item) => {
              return item.id?.toString() === docID
            })
          } else if (location.pathname.startsWith('/notes')) {
            const notes = await import(
              '../../Assets/JSON/AcademicData/Notes/notes.json'
            )
            const allNotes = notes.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allNotes.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/library')) {
            const notes = await import(
              '../../Assets/JSON/AcademicData/Library/library.json'
            )
            const allNotes = notes.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allNotes.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/nepiksidppage')) {
            const NepIksIdp = await import(
              '../../Assets/JSON/Aboutjson/NepIksIdp.json'
            )
            const NepIksIdpData = NepIksIdp.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = NepIksIdpData.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/iqacmeetings')) {
            const notes = await import(
              '../../Assets/JSON/IQAC/meetingsIQAC.json'
            )
            const allNotes = notes.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allNotes.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/co&po')) {
            const notes = await import(
              '../../Assets/JSON/AcademicData/CosAndPos/cosandpos.json'
            )
            const allNotes = notes.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allNotes.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/IQACDocuments')) {
            const IQACDocuments = await import(
              '../../Assets/JSON/IQAC/IQAC_Documents.json'
            )
            const allIQACDocuments = IQACDocuments.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allIQACDocuments.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/nonstatutorycommittees')) {
            const IQACDocuments = await import(
              '../../Assets/JSON/Committees/nonstatutorycommittees.json'
            )
            const allIQACDocuments = IQACDocuments.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allIQACDocuments.find((item) => {
              return item.ID?.toString() === docID
            })
          } else if (location.pathname.startsWith('/bestpractice')) {
            const BPDoc = await import(
              '../../Assets/JSON/Aboutjson/Best_Practice.json'
            )
            const BPData = BPDoc.default.BestPractices_Data

            // Flatten all documents from all sections
            const allDocs = BPData.flatMap((section) =>
              Object.values(section.URLs || {}).flat()
            )

            // Find the document by its id (101 in your example)
            fallbackData = allDocs.find((doc) => doc.id?.toString() === docID)

            if (!fallbackData) {
              console.error('Document not found with id:', docID)
              // Handle error (show message, redirect, etc.)
            }
          } else if (location.pathname.startsWith('/distinctivefeaturepage')) {
            const distinctivefeature = await import(
              '../../Assets/JSON/Aboutjson/Distinctive_Feature.json'
            )
            const DistinctiveFeatureData =
              distinctivefeature.default.DistinctiveFeature_Data

            // Flatten all documents from all sections
            const allDocs = DistinctiveFeatureData.flatMap((section) =>
              Object.values(section.URLs || {}).flat()
            )

            // Find the document by its id (101 in your example)
            fallbackData = allDocs.find((doc) => doc.id?.toString() === docID)

            if (!fallbackData) {
              console.error('Document not found with id:', docID)
              // Handle error (show message, redirect, etc.)
            }
          } else if (location.pathname.startsWith('/nirf')) {
            const nirf = await import(
              '../../Assets/JSON/NIRF/NIRFExtended_Data.json'
            )
            const nirfData = nirf.default.NIRFExtended_Data

            // Match the section object by outer ID
            const target = nirfData.find(
              (item) => item.ID?.toString() === docID
            )

            if (target?.URLs) {
              // Collect all docs from all available arrays in `URLs`
              const allDocs = Object.values(target.URLs)
                .filter((val) => Array.isArray(val))
                .flat()

              // Find the specific doc by its internal id
              fallbackData = allDocs.find((doc) => doc.id?.toString() === id)
            }
          } else if (location.pathname.startsWith('/institutionalpolicy')) {
            const notes = await import(
              '../../Assets/JSON/Institutionalpolicy/Institutionalpolicy.json'
            )
            const allNotes = notes.default.flatMap(
              (group) => group.documents || []
            )
            fallbackData = allNotes.find((item) => {
              return item.ID?.toString() === docID
            })
          }

          // If still not found, try your original fallback API logic
          if (!fallbackData) {
            const result = await fetchData(
              `naacdetails?organization_id=${orgId}&sub_organization_id=${subOrgId}`
            )
            const supportingDoc = result?.rows?.find(
              (r) => r.title === 'Supporting Documents'
            )
            if (supportingDoc?.external_id) {
              const extendedResult = await fetchData(
                `naacdetails/${supportingDoc.external_id}?ExternalID=${id}&organization_id=${orgId}&sub_organization_id=${subOrgId}`
              )
              fallbackData = extendedResult.NaacExtended_Data.find(
                (data) => data.external_id?.toString() === id
              )
            }
          }

          setSelectedDocData(fallbackData || null)
        }
      } catch (error) {
        console.error('Error loading document:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDocumentData()
  }, [location.state, id, docID, orgId, subOrgId, location.pathname])

  // Auto download effect
  useEffect(() => {
    if (shouldAutoDownload && !loading && selectedDocData && !downloading) {
      handleDownload()
    }
  }, [shouldAutoDownload, loading, selectedDocData, downloading]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackClick = () => {
    if (location.state?.fromList) {
      navigate(location.state.fromList)
    } else {
      navigate(backPath)
    }
  }

  const fileUrl = selectedDocData?.pathurl || selectedDocData?.path_url
  const fullFileUrl = fileUrl ? `${IMAGE_BASE_PATH_DBRALC}${fileUrl}` : ''
  const isPdf = fileUrl?.endsWith('.pdf')
  const isImage = /\.(jpeg|jpg|png|gif|bmp)$/i.test(fileUrl)
  const isOffice = /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(fileUrl)

  if (loading) return <Loader />
  if (!fileUrl) return <NoFile handleBackClick={handleBackClick} />

  // Don't render the viewer if we're auto-downloading
  if (shouldAutoDownload) {
    return (
      <Loader
        message={downloading ? 'Downloading file...' : 'Preparing download...'}
      />
    )
  }

  return (
    <section className="container my-4">
      <Header
        filename={selectedDocData?.[filenameKey]}
        handleBackClick={handleBackClick}
        canDownload={canDownload}
        handleDownload={handleDownload}
        downloading={downloading}
      />
      <ViewerSection
        fullFileUrl={fullFileUrl}
        isPdf={isPdf}
        isImage={isImage}
        isOffice={isOffice}
      />
    </section>
  )
}

const Loader = ({ message = 'Loading...' }) => (
  <Box textAlign="center" my={4}>
    <CircularProgress />
    <Typography variant="h6" mt={2}>
      {message}
    </Typography>
  </Box>
)

const NoFile = ({ handleBackClick }) => (
  <Box textAlign="center" my={6}>
    <Typography variant="h2">No file available.</Typography>
    <Button
      variant="outlined"
      onClick={handleBackClick}
      startIcon={<ArrowBackIcon />}
    >
      Back to List
    </Button>
  </Box>
)

const Header = ({
  filename,
  handleBackClick,
  canDownload,
  handleDownload,
  downloading,
  isMobile,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2,
      width: '100%',
      gap: 2,
    }}
  >
    {/* Back Button - Left Side */}
    <Button
      variant="contained"
      onClick={handleBackClick}
      startIcon={<ArrowBackIcon />}
      size={isMobile ? 'small' : 'medium'}
      sx={{ flexShrink: 0 }}
    >
      Back
    </Button>

    {/* Filename - Centered */}
    <Typography
      variant="h5"
      sx={{
        textAlign: 'center',
        flexGrow: 1,
        fontSize: { xs: '1.1rem', sm: '1.5rem' },
        px: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {filename}
    </Typography>

    {/* Download Button - Right Side */}
    {canDownload ? (
      <Button
        variant="contained"
        color="primary"
        onClick={handleDownload}
        startIcon={
          downloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <DownloadIcon />
          )
        }
        disabled={downloading}
        size={isMobile ? 'small' : 'medium'}
        sx={{ flexShrink: 0 }}
      >
        {isMobile
          ? '' // show only icon
          : downloading
          ? 'Downloading...'
          : 'Download'}
      </Button>
    ) : (
      <Tooltip title="Download not available for this document">
        <span>
          <Button
            variant="contained"
            color="primary"
            disabled
            startIcon={<DownloadIcon />}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flexShrink: 0 }}
          >
            {isMobile ? '' : 'Download'}
          </Button>
        </span>
      </Tooltip>
    )}
  </Box>
)

const ViewerSection = ({ fullFileUrl, isPdf, isImage, isOffice }) => (
  <Box textAlign="center" width="100%" height="100vh">
    {isPdf ? (
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
        <Viewer fileUrl={fullFileUrl} />
      </Worker>
    ) : isImage ? (
      <img
        src={fullFileUrl}
        alt="Document Preview"
        loading="lazy"
        placeholder="blur"
        style={{ maxWidth: '100%', height: 'auto', maxHeight: '500px' }}
      />
    ) : isOffice ? (
      <iframe
        src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
          fullFileUrl
        )}`}
        title="Office Document Viewer"
        width="100%"
        height="600px"
        style={{ border: 'none' }}
      />
    ) : (
      <Typography variant="h6" color="error">
        Unsupported file format.
      </Typography>
    )}
  </Box>
)

export default FileViewer
